/* eslint-disable no-await-in-loop */
import { devLog } from '@/utils/logger';

/**
 * OPFS错误处理工具
 * 统一处理Origin Private File System相关的错误和重试逻辑
 */

export interface OPFSErrorHandlerOptions {
  maxRetries?: number;
  retryDelay?: number;
  onError?: (error: Error, context: string) => void;
}

class OPFSErrorHandler {
  // eslint-disable-next-line no-use-before-define
  private static instance: OPFSErrorHandler | null = null;
  private retryCounts = new Map<string, number>();
  private readonly defaultOptions: Required<OPFSErrorHandlerOptions> = {
    maxRetries: 3,
    retryDelay: 200,
    onError: (error, context) => {
      devLog.warn(`[OPFSErrorHandler] ${context}:`, error);
    }
  };

  private constructor() {
    // 私有构造函数，防止外部实例化
    // 初始化重试计数映射
    this.retryCounts = new Map<string, number>();
  }

  static getInstance(): OPFSErrorHandler {
    if (!OPFSErrorHandler.instance) {
      OPFSErrorHandler.instance = new OPFSErrorHandler();
    }
    return OPFSErrorHandler.instance;
  }

  /**
   * 包装可能产生OPFS错误的异步操作
   */
  async withRetry<T>(
    operation: () => Promise<T>,
    context: string,
    options: OPFSErrorHandlerOptions = {}
  ): Promise<T> {
    const opts = { ...this.defaultOptions, ...options };
    const operationKey = `${context}_${Date.now()}`;
    
    for (let attempt = 0; attempt <= opts.maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        if (error instanceof Error && error.name === 'NoModificationAllowedError') {
          if (attempt < opts.maxRetries) {
            opts.onError(error, `${context} (尝试 ${attempt + 1}/${opts.maxRetries + 1})`);
            await this.delay(opts.retryDelay * (attempt + 1)); // 递增延迟
          } else {
            opts.onError(error, `${context} (最终失败)`);
            throw new Error(`OPFS操作失败，已重试${opts.maxRetries}次: ${error.message}`);
          }
        } else {
          throw error;
        }
      }
    }
    
    throw new Error(`OPFS操作失败: ${context}`);
  }

  /**
   * 清理指定上下文的重试计数
   */
  clearRetryCount(context: string): void {
    this.retryCounts.delete(context);
  }

  /**
   * 清理所有重试计数
   */
  clearAllRetryCounts(): void {
    this.retryCounts.clear();
  }

  /**
   * 延迟函数
   */
  private delay(ms: number): Promise<void> {
    return new Promise(resolve => {
      setTimeout(resolve, ms);
    });
  }

  /**
   * 检查是否为OPFS相关错误
   */
  static isOPFSError(error: any): boolean {
    return error instanceof Error 
           && (error.name === 'NoModificationAllowedError' 
            || error.message.includes('Access Handles cannot be created'));
  }

  /**
   * 获取OPFS错误信息
   */
  static getOPFSErrorInfo(error: Error): { type: string; message: string } {
    if (error.name === 'NoModificationAllowedError') {
      return {
        type: 'ACCESS_CONFLICT',
        message: '文件访问冲突，可能有其他进程正在访问同一文件'
      };
    }
    
    if (error.message.includes('Access Handles cannot be created')) {
      return {
        type: 'HANDLE_CREATION_FAILED',
        message: '无法创建文件访问句柄'
      };
    }
    
    return {
      type: 'UNKNOWN',
      message: error.message
    };
  }
}

// 导出单例实例
// 导出类以便类型检查
export { OPFSErrorHandler };

// 导出单例实例
export const opfsErrorHandler = OPFSErrorHandler.getInstance(); 